<template>
    <div class="module">
        <slot name="header">
            <header class="module__header--with-disclaimer">
                <h1>{{ title }}</h1>
                <slot name="disclaimer"></slot>
            </header>
        </slot>

        <section
            v-if="hasCheckboxes() || createText || hasOption('importable') || $slots.create"
            class="module__actions"
        >
            <div>
                <slot name="create"></slot>
                <router-link
                    v-if="createText"
                    :to="`${routeModel}/create${filterQueryString}`"
                    class="btn btn-primary"
                    data-test-id="create-model-button"
                    tag="button"
                >
                    {{ createText }}
                </router-link>
            </div>

            <div>
                <span v-show="hasMultiItemActions && selected.length > 0">
                    <component
                        :is="action"
                        v-for="(action, index) in multiItemActions"
                        :key="index"
                        :ids="selected"
                        @reload="loadData"
                    />
                </span>

                <button
                    v-if="selected.length > 0 && hasOption('deletable')"
                    class="btn"
                    data-test-id="delete-selected-button"
                    @click.prevent="deleteSelected()"
                >
                    <span class="icon-delete" /> Delete Selected
                </button>

                <router-link
                    v-if="hasOption('importable')"
                    :to="`${routeModel}/upload${filterQueryString}`"
                    class="btn"
                    data-test-id="bulk-import-button"
                    tag="button"
                >
                    <span class="icon-export" /> Bulk import
                </router-link>

                <slot name="custom-action" />
            </div>
        </section>

        <section class="module__search">
            <section class="filters">
                <search-bar
                    v-if="showTextSearch"
                    :prepopulate="searchTerm"
                    @search="search"
                />

                <slot name="search" />
            </section>

            <pagination
                :currentPage="Number(pagination.currentPage)"
                :lastPage="Number(pagination.lastPage)"
                :model="paginationTitle ? lowerCase(paginationTitle) : lowerCase(title)"
                :perPage="Number(pagination.perPage)"
                :total="Number(pagination.total)"
                @paginate="paginationChanged($event)"
            />
        </section>

        <section
            v-if="$slots.navigation"
            class="module__navigation"
        >
            <slot name="navigation"></slot>
        </section>

        <section class="module__body">
            <div
                v-if="loading"
                class="loader large"
            />

            <div
                v-else
                class="module__table"
            >
                <table
                    v-if="rows.length"
                    :class="[{ 'index-view': !hasOptions }, `align-cells-${alignCells}`]"
                    class="index"
                >
                    <thead>
                        <tr>
                            <th
                                v-if="hasCheckboxes() && !disableMultiSelect"
                                class="actions-cell"
                                scope="col"
                            >
                                <label for="checkbox-all">
                                    <input
                                        id="checkbox-all"
                                        :checked="selected.length === rows.length && rows.length > 0"
                                        data-test-id="checkbox-all"
                                        type="checkbox"
                                        @click="toggleAllRows"
                                    />
                                </label>
                            </th>
                            <th
                                v-for="(col, index) in cols"
                                :key="index"
                                :class="{
                                    active: sort.col === col.value,
                                    asc: sort.dir === 1,
                                    desc: sort.dir === -1,
                                    sortable: col.sortable,
                                }"
                                :data-test-id="`column-header-${col.value}`"
                                scope="col"
                                @click="sortBy(col)"
                            >
                                {{ col.label
                                }}<span
                                    v-if="col.sortable"
                                    class="icon-sort"
                                />

                                <info-tooltip v-if="col.tooltip">{{ col.tooltip }}</info-tooltip>
                            </th>
                            <th
                                v-if="hasOptions"
                                scope="col"
                            />
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="row in rows"
                            :key="row.id"
                            :data-test-id="`row-${row.id}`"
                        >
                            <td v-if="hasCheckboxes() && !disableMultiSelect">
                                <label :for="'checkbox-' + row.id">
                                    <input
                                        :id="'checkbox-' + row.id"
                                        v-model="selected"
                                        :data-test-id="`row-select-${row.id}`"
                                        :value="row.id"
                                        type="checkbox"
                                    />
                                </label>
                            </td>

                            <td
                                v-for="(col, index) in cols"
                                :key="index"
                                :data-test-id="`row-${row.id}-cell-${index}`"
                            >
                                <custom-cell-wrapper
                                    v-if="'customCell' in col"
                                    :col="col"
                                    :componentFile="`${col.customCell}`"
                                    :customEvents="col.customEvents"
                                    :customProps="col.customProps"
                                    :items="row[col.value]"
                                    :row="row"
                                    :total="getItemsCount(row, col.value)"
                                    @get-items="retrieveAllRelationItems"
                                />

                                <array-table-cell
                                    v-else-if="row[col.value] instanceof Array"
                                    :col="col"
                                    :items="row[col.value]"
                                    :objectId="row.id"
                                    :total="getItemsCount(row, col.value)"
                                    @get-items="retrieveAllRelationItems"
                                />

                                <object-table-cell
                                    v-else-if="row[col.value] instanceof Object"
                                    :col="col"
                                    :item="row[col.value]"
                                />

                                <span
                                    v-else-if="col.filter"
                                    :title="format(row[col.value], col.filter)"
                                >
                                    {{ format(row[col.value], col.filter) }}
                                </span>

                                <span
                                    v-else
                                    :title="row[col.value]"
                                >
                                    {{ row[col.value] }}
                                </span>
                            </td>

                            <td
                                v-if="hasOptions || optionsComponent"
                                :data-test-id="`row-${row.id}-actions`"
                                class="actions"
                            >
                                <custom-cell-wrapper
                                    v-if="optionsComponent"
                                    :componentFile="optionsComponent"
                                    :row="row"
                                    @reload="loadData()"
                                />
                                <router-link
                                    v-if="hasOption('viewable')"
                                    :data-test-id="`row-${row.id}-action-view`"
                                    :to="`${row['slug']}/dashboard`"
                                    class="view icon-view"
                                    tag="a"
                                    title="View"
                                />

                                <router-link
                                    v-if="hasOption('editable')"
                                    :data-test-id="`row-${row.id}-action-edit`"
                                    :to="`${routeModel}/${row.id}/edit`"
                                    class="edit icon-edit"
                                    tag="a"
                                    title="Edit"
                                />

                                <a
                                    v-if="hasOption('deletable')"
                                    :data-test-id="`row-${row.id}-action-delete`"
                                    class="delete icon-delete"
                                    href="#"
                                    title="Delete"
                                    @click.prevent="remove([row.id])"
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>

                <p
                    v-if="!rows.length"
                    class="no-results"
                >
                    <span v-if="noResultsText">{{ noResultsText }}</span>
                    <span v-else>No results found.</span>
                </p>
            </div>
        </section>
    </div>
</template>

<script>
import _ from 'lodash'
import canConfirm from '@/mixins/canConfirm'
import ArrayTableCell from '@/components/IndexTable/ArrayTableCell.vue'
import ObjectTableCell from '@/components/IndexTable/ObjectTableCell.vue'
import SearchBar from '@/components/IndexTable/SearchBar.vue'
import Pagination from '@/components/IndexTable/Pagination.vue'
import CustomCellWrapper from '@/components/IndexTable/CustomCellWrapper.vue'
import InfoTooltip from '@/components/InfoTooltip.vue'

import UserGroupsActionButton from '@/components/IndexTable/custom/actions/UserGroupsActionButton.vue'
import GenerateQRCodeAction from '@/components/IndexTable/custom/actions/GenerateQRCodeAction.vue'

import sortDirections from '@/constants/sort-directions'
import canToast from '@/mixins/canToast'
import stringFormatting from '@/mixins/stringFormatting'
import needsOrganizationSlug from '@/mixins/needsOrganizationSlug'

export default {
    name: 'IndexTable',

    components: {
        ArrayTableCell,
        UserGroupsActionButton,
        CustomCellWrapper,
        ObjectTableCell,
        GenerateQRCodeAction,
        InfoTooltip,
        SearchBar,
        Pagination,
    },

    mixins: [canConfirm, canToast, stringFormatting, needsOrganizationSlug],

    props: {
        title: String,
        model: String,
        fetchModel: String,
        filters: Object,
        cols: Array,
        createText: String,
        options: Array,
        multiItemActions: Array,
        defaultSort: String,
        defaultDir: String,
        perPage: Number,
        paginationTitle: String,
        optionsComponent: String,
        deleteHandler: Function,
        noResultsText: String,
        deleteSingleText: String,
        deleteManyText: String,
        disableMultiSelect: {
            type: Boolean,
            default: false,
        },
        alignCells: {
            type: String,
            default: 'top',
        },
        showTextSearch: {
            type: Boolean,
            default: true,
        },
    },

    data() {
        return {
            rows: [],
            selected: [],
            pagination: {
                currentPage: this.$route.query.page || 1,
                perPage: this.$route.query.per_page || this.perPage || 25,
                lastPage: 0,
                total: 0,
            },
            sort: {
                col: this.$route.query.sort || this.defaultSort || this.cols[0].value,
                dir: this.$route.query.dir || this.defaultDir || sortDirections.ASC,
            },
            searchTerm: this.$route.query.search_term || '',
            loading: true,
        }
    },

    computed: {
        hasOptions() {
            if (
                !this.optionsComponent &&
                (typeof this.options === 'undefined' || !this.options || !this.options.length)
            ) {
                return false
            }

            return true
        },

        hasMultiItemActions() {
            const actions = this.multiItemActions
            if (typeof actions === 'undefined' || !actions || !actions.length) {
                return false
            }
            return true
        },

        queryParams() {
            const searchParams = new URLSearchParams()

            if (this.searchTerm.length > 0) {
                searchParams.append('search_term', this.searchTerm)
            }

            searchParams.append('page', this.pagination.currentPage)
            searchParams.append('per_page', this.pagination.perPage)
            searchParams.append('sort', this.sort.col)
            searchParams.append('dir', this.sort.dir)

            if (this.filters) {
                Object.entries(this.filters).forEach(([key, value]) => {
                    searchParams.append(key, value)
                })
            }

            return searchParams
        },

        queryString() {
            return this.queryParams.toString()
        },

        filterQueryString() {
            const searchParams = new URLSearchParams()
            if (this.filters) {
                Object.entries(this.filters).forEach(([key, value]) => {
                    searchParams.append(key, value)
                })

                return `?${searchParams.toString()}`
            }

            return ''
        },

        routeModel() {
            if (this.organizationSlug) {
                return `/${this.organizationSlug}/${this.model}`
            }

            return `/${this.model}`
        },
    },

    watch: {
        '$route.query': {
            handler(query) {
                this.pagination = {
                    currentPage: query.page || 1,
                    perPage: query.per_page || this.perPage || 25,
                }
                this.sort = {
                    col: query.sort || this.defaultSort || this.cols[0].value,
                    dir: query.dir || this.defaultDir || sortDirections.ASC,
                }
                this.searchTerm = query.search_term || ''
                this.loadData()
            },
            flush: 'post',
        },

        filters(newValue) {
            this.pagination.currentPage = 1
            this.pushQueryUpdatesToHistory()
            this.loadData()
        },
    },

    mounted() {
        this.loadData()
    },

    methods: {
        getItemsCount(row, value) {
            value += '_count'
            return row[value]
        },

        hasCheckboxes() {
            return this.hasMultiItemActions || this.hasOption('deletable')
        },

        format(value, filter) {
            const filters = Array.isArray(filter) ? filter : [filter]
            return filters.reduce((acc, tmpFilter) => this.$filters[tmpFilter](acc), value)
        },

        hasOption(option) {
            return this.hasOptions && _.includes(this.options, option)
        },

        toggleAllRows() {
            if (this.selected.length < this.rows.length) {
                this.selected = this.rows.map((r) => r.id)
                return
            }

            this.selected = []
        },

        search(searchTerm) {
            this.searchTerm = searchTerm
            this.pagination.currentPage = 1
            this.pushQueryUpdatesToHistory()
        },

        sortBy(col) {
            if (!col.sortable) {
                return
            }

            if (this.sort.col === col.value) {
                this.sort.dir = this.sort.dir === sortDirections.ASC ? sortDirections.DESC : sortDirections.ASC
            } else {
                this.sort.dir = sortDirections.ASC
                this.sort.col = col.value
            }

            this.pushQueryUpdatesToHistory()
        },

        pushQueryUpdatesToHistory() {
            this.$router.push(`?${this.queryString}`)
        },

        replaceCurrentHistoryStateWithQueryUpdates() {
            this.$router.replace(`?${this.queryString}`)
        },

        deleteSelected() {
            if (this.selected.length > 0) {
                this.remove(this.selected)
            }
        },

        remove(ids) {
            if (this.deleteHandler) {
                this.handleCustomDelete(ids)
            } else {
                this.showDeleteConfirmation(ids)
            }
        },

        // call custom delete handler if it exists
        handleCustomDelete(ids) {
            if (this.deleteHandler) {
                const idMap = {}

                ids.forEach((id) => {
                    idMap[id] = true
                })

                const items = this.rows.filter((row) => row.id in idMap)

                if (items.length !== ids.length) {
                    const showTitle = this.lowerCase(this.singularize(this.title))
                    this.toast(`Could not delete ${showTitle}: Oops! Something went wrong`, 'error')
                }

                // call custom handler with
                //  items being deleted
                //  default delete handler
                //  success callback to be called after deletion
                this.deleteHandler(items, this.showDeleteConfirmation, this.performDelete)
            }
        },

        async showDeleteConfirmation(ids) {
            let showTitle = ''
            let text = ''

            if (ids.length > 1) {
                showTitle = this.lowerCase(this.title)
                text = this.deleteManyText || `Are you sure you want to delete these ${showTitle}?`
            } else {
                showTitle = this.lowerCase(this.singularize(this.title))
                text = this.deleteSingleText || `Are you sure you want to delete this ${showTitle}?`
            }

            const confirmed = await this.confirm({
                message: text,
                acceptLabel: 'Delete',
                danger: true,
            })

            if (confirmed) {
                this.performDelete(ids)
            }
        },

        performDelete(ids) {
            let request
            let { title } = this

            if (ids.length > 1) {
                request = this.$http.post(`${this.organizationSlug}/${this.model}/delete-many`, {
                    [this.model]: ids,
                })
            } else {
                request = this.$http.delete(`${this.organizationSlug}/${this.model}/${ids[0]}`)
                title = this.singularize(title)
            }

            request
                .then(() => {
                    this.toast(`${title} successfully deleted!`)
                    this.loadData()
                })
                .catch((error) => {
                    const lowercaseTitle = this.lowerCase(title)

                    if (error.response.status === 500) {
                        this.toast(`Could not delete ${lowercaseTitle}`, 'error')
                    } else {
                        const { errors } = error.response.data
                        const message = errors[Object.keys(errors)[0]][0]
                        this.toast(`Could not delete ${lowercaseTitle}: ${message}`, 'error')
                    }
                })
        },

        lowerCase(string) {
            const words = string.split(' ')
            return words.map((word) => word[0].toLowerCase() + word.substring(1)).join(' ')
        },

        loadData() {
            this.loading = true
            this.selected = []

            const model = this.fetchModel ? this.fetchModel : this.model

            const url = `${this.organizationSlug}/${model}?${this.queryParams.toString()}`
            this.$http.get(url).then(({ data }) => {
                if (data.current_page > data.last_page) {
                    this.pagination.currentPage = data.last_page
                    this.replaceCurrentHistoryStateWithQueryUpdates()
                }

                this.pagination = {
                    total: data.total,
                    perPage: data.per_page,
                    currentPage: data.current_page,
                    lastPage: data.last_page,
                }

                this.rows = data.data

                this.loading = false
            })
        },

        retrieveAllRelationItems(col, modelId) {
            const { value, fetchAllAttribute } = col

            const model = this.fetchModel ? this.fetchModel : this.model
            const url = `${this.organizationSlug}/${model}/${modelId}/${fetchAllAttribute ?? value}`

            this.$http.get(url).then(({ data }) => {
                for (let i = 0; i < this.rows.length; i++) {
                    if (this.rows[i].id === modelId) {
                        this.rows[i][value] = data
                    }
                }
            })
        },

        paginationChanged(newPagination) {
            this.pagination = newPagination
            this.pushQueryUpdatesToHistory()
        },
    },
}
</script>
